import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent,
} from "react";

import type { FilmCard } from "../../api/types";
import { safeCssUrl } from "../../lib/url";
import { LEAVE_MS, classifyRelease, dragTransform, glowShadow, type Release } from "../swipe";

interface Props {
  card: FilmCard;
  /** Выбор сделан: карточку нельзя ни переворачивать, ни листать. */
  frozen?: boolean;
  onVote: (kind: "like" | "dislike") => void;
}

/** Постер; по тапу переворачивается и показывает описание; свайп вправо — лайк, влево — дизлайк. */
export function SwipeCard({ card, frozen = false, onVote }: Props) {
  const [flipped, setFlipped] = useState(false);
  const [dx, setDx] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [leaving, setLeaving] = useState<"like" | "dislike" | null>(null);
  const startX = useRef(0);
  const dxRef = useRef(0);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => void (leaveTimer.current && clearTimeout(leaveTimer.current)), []);

  const leave = (kind: "like" | "dislike") => {
    if (leaving || frozen) return;
    setDragging(false);
    setDx(0);
    setLeaving(kind);
    leaveTimer.current = setTimeout(() => onVote(kind), LEAVE_MS);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (frozen || leaving) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    startX.current = e.clientX;
    dxRef.current = 0;
    setDragging(true);
  };
  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    if (!dragging) return;
    dxRef.current = e.clientX - startX.current;
    setDx(dxRef.current);
  };
  const onPointerUp = () => {
    if (!dragging) return;
    const release: Release = classifyRelease(dxRef.current);
    setDragging(false);
    setDx(0);
    dxRef.current = 0;
    if (release === "tap") setFlipped((f) => !f);
    else if (release === "like" || release === "dislike") leave(release);
  };
  const onPointerCancel = () => {
    setDragging(false);
    setDx(0);
    dxRef.current = 0;
  };
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (frozen) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setFlipped((f) => !f);
    } else if (e.key === "ArrowRight") leave("like");
    else if (e.key === "ArrowLeft") leave("dislike");
  };

  const poster = safeCssUrl(card.poster_url);
  const showBack = flipped && !frozen;
  const classes = [
    "card",
    showBack && "card--back",
    dragging && "card--dragging",
    leaving && `card--${leaving}`,
    frozen && "card--frozen",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      className={classes}
      role="button"
      tabIndex={0}
      aria-label={showBack ? `${card.title}: описание` : card.title}
      style={{
        ...(poster && !showBack ? { backgroundImage: poster } : {}),
        transform: dragTransform(dx),
        boxShadow: glowShadow(dx),
      }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerCancel}
      onKeyDown={onKeyDown}
    >
      {showBack ? (
        <CardDetails card={card} />
      ) : (
        !poster && (
          <div className="card__noposter">
            <span>{card.title}</span>
            {card.year ? <span>{card.year}</span> : null}
          </div>
        )
      )}
    </div>
  );
}

function CardDetails({ card }: { card: FilmCard }) {
  const rating = `${card.rating_kinopoisk || "-"} / ${card.rating_imdb || "-"}`;
  const fields: [string, string | number | null | undefined][] = [
    ["Название", card.title],
    ["Год", card.year],
    ["Длительность мин.", card.duration],
    ["Жанр", card.genres.join(", ")],
    ["Рейтинг Кинопоиск/IMDb", rating],
  ];
  return (
    <>
      {fields.map(([label, value]) => (
        <p key={label}>
          <b>{label}: </b>
          <span>{value === null || value === undefined || value === "" ? "-" : value}</span>
        </p>
      ))}
      <p>
        <b>Описание: </b>
        <FitText text={card.description ?? "-"} />
      </p>
    </>
  );
}

/** Описание уменьшает шрифт (16 → 8 px), пока помещается в 6 строк; остальное обрезает CSS. */
function FitText({ text }: { text: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    let size = 16;
    el.style.fontSize = `${size}px`;
    while (el.scrollHeight > el.clientHeight + 1 && size > 8) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
  }, [text]);
  return (
    <span ref={ref} className="card__description">
      {text}
    </span>
  );
}
